import React, { useState } from 'react';
import BottomModal from './BottomModal';
import WorkflowButton from './WorkflowButton';
import { WorkflowListSection, WorkflowListItem } from '../../types/workflow.types';

interface WorkflowListProps {
  header?: string;
  body?: string;
  footer?: string;
  buttonText?: string;
  sections: WorkflowListSection[];
  onItemSelect: (itemId: string, itemTitle?: string) => void;
  disabled?: boolean;
  className?: string;
}

const WorkflowList: React.FC<WorkflowListProps> = ({
  header,
  body,
  footer,
  buttonText = 'Select',
  sections,
  onItemSelect,
  disabled = false,
  className = ''
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkflowListItem | null>(null);

  const handleItemClick = (item: WorkflowListItem) => {
    if (!disabled) {
      setSelectedItem(item);
      setIsModalOpen(false);
      onItemSelect(item.id, item.title);
    }
  };

  const toggleModal = () => {
    if (!disabled) {
      setIsModalOpen(!isModalOpen);
    }
  };

  // Convert WorkflowListSection to BottomModalSection format
  const modalSections = sections.map(section => ({
    title: section.title,
    items: section.items.map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      subtitle: '',
      customContent: null
    }))
  }));

  return (
    <div className={`workflow-list ${className}`}>
      {/* Header */}
      {header && (
        <div className="text-sm font-semibold text-gray-800 px-3 py-3">
          {header}
        </div>
      )}

      {/* Body */}
      {body && (
        <div className="text-sm text-gray-600 px-3 mb-3">
          {body}
        </div>
      )}

      {/* List Button */}
      <WorkflowButton
        buttons={[{
          id: 'list-button',
          label: selectedItem ? selectedItem.title : buttonText
        }]}
        onButtonClick={(buttonId) => {
          if (buttonId === 'list-button') {
            toggleModal();
          }
        }}
        disabled={disabled}
      />

      {/* Bottom Modal */}
      <BottomModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={buttonText}
        sections={modalSections}
        selectedItem={selectedItem ? {
          id: selectedItem.id,
          title: selectedItem.title,
          description: selectedItem.description,
          subtitle: '',
          customContent: null
        } : null}
        onItemSelect={(item) => handleItemClick(item as any)}
        showSearch={sections.length > 1 || (sections[0]?.items?.length ?? 0) > 5}
        containerPositioned={true}
      />

      {/* Footer */}
      {footer && (
        <div className="text-xs text-gray-500 py-3 px-3 bg-gray-50">
          {footer}
        </div>
      )}
    </div>
  );
};

export default WorkflowList;
